import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { useGetUsersQuery, useLazyGetUsersQuery, useDeleteUserMutation, useToggleUserStatusMutation, useGetCountriesQuery } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import Image from '../../../components/Image';
import DateRangePicker from '../../../components/DateRangePicker';
import ActionButtons from '../../../components/ActionButtons';
import Pagination from '../../../components/Pagination';
import { formatDateOnly } from '../../../utils/helpers';
import { showSuccess, showError } from '../../../utils/toast';

const VERIFICATION_OPTIONS = [
  { value: '', label: 'All Verifications' },
  { value: 'Green', label: 'Green' },
  { value: 'Gold', label: 'Gold' },
  { value: 'Unverified', label: 'Unverified' },
];

const CSV_HEADERS = ['#', 'Name', 'Email', 'Phone', 'Country', 'City',  'Status', 'Created Date'];

function getUserRow(user: any, idx: number): string[] {
  return [
    String(idx + 1),
    user.name || '',
    user.email || '',
    user.phone || '',
    user.country?.name || '',
    user.city?.name || '',
    user.accStatus === 1 ? 'Active' : 'Inactive',
    user.created_at ? new Date(user.created_at).toLocaleDateString() : '',
  ];
}

function downloadCSV(rows: string[][], filename: string) {
  const content = rows
    .map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob(['\ufeff' + content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const AdminUsers: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [daterange, setDaterange] = useState('');
  const [verificationBadge, setVerificationBadge] = useState('');
  const [countryId, setCountryId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const { data: countriesData } = useGetCountriesQuery({ per_page: 1000 });
  const countries = countriesData?.data?.data || countriesData?.data || [];
  const countryOptions = [
    { value: null, label: 'All Countries' },
    ...countries.map((c: any) => ({ value: c.id, label: c.name || c.name_en })),
  ];

  const filters = { search, daterange, verification_badge: verificationBadge, country_id: countryId, page };
  const { data, isLoading, error, refetch } = useGetUsersQuery(filters);
  const [fetchAllUsers] = useLazyGetUsersQuery();
  const [deleteUser] = useDeleteUserMutation();
  const [toggleUserStatus] = useToggleUserStatusMutation();

  const hasActiveFilters = search || daterange || verificationBadge || countryId;

  const handleClear = () => {
    setSearch('');
    setDaterange('');
    setVerificationBadge('');
    setCountryId(null);
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const result = await fetchAllUsers({ search, daterange, verification_badge: verificationBadge, country_id: countryId, per_page: 10000, page: 1 }).unwrap();
      const allUsers: any[] = result?.data?.data || result?.data || [];
      const rows = [CSV_HEADERS, ...allUsers.map((user, idx) => getUserRow(user, idx))];
      const timestamp = new Date().toISOString().slice(0, 10);
      downloadCSV(rows, `users_${timestamp}.csv`);
    } catch {
      showError('Failed to export users. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [search, daterange, verificationBadge, countryId]);

  const handleDelete = async (id: number) => {
    try {
      const result = await deleteUser(id).unwrap();
      showSuccess(result.message || 'User deleted successfully!');
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to delete user. Please try again.');
    }
  };

  const handleToggleStatus = async (id: number, currentStatus: number) => {
    try {
      const newStatus = currentStatus === 1 ? 0 : 1;
      const result = await toggleUserStatus({ id, status: newStatus }).unwrap();
      showSuccess(result.message || 'User status updated successfully!');
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to update user status. Please try again.');
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  if (error) {
    return <div className="alert alert-danger m-5">Error loading users</div>;
  }

  const users = data?.data || { data: [], current_page: 1, last_page: 1 };

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Customers</h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" className="text-muted text-hover-primary">Home</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">users</li>
              </ul>
            </div>
            <div className="d-flex align-items-center gap-2 gap-lg-3">
              <button
                type="button"
                className="btn btn-sm btn-light-primary me-3"
                onClick={handleExportCSV}
                disabled={exporting}
              >
                {exporting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Exporting...
                  </>
                ) : (
                  <>
                    <span className="svg-icon svg-icon-2">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect opacity="0.3" x="12.75" y="4.25" width="12" height="2" rx="1" transform="rotate(90 12.75 4.25)" fill="currentColor"></rect>
                        <path d="M12.0573 6.11875L13.5203 7.87435C13.9121 8.34457 14.6232 8.37683 15.056 7.94401C15.4457 7.5543 15.4641 6.92836 15.0979 6.51643L12.4974 3.59084C12.0996 3.14332 11.4004 3.14332 11.0026 3.59084L8.40206 6.51643C8.0359 6.92836 8.0543 7.5543 8.44401 7.94401C8.87683 8.37683 9.58785 8.34458 9.9797 7.87435L11.4427 6.11875C11.6026 5.92684 11.8974 5.92684 12.0573 6.11875Z" fill="currentColor"></path>
                        <path opacity="0.3" d="M18.75 8.25H17.75C17.1977 8.25 16.75 8.69772 16.75 9.25C16.75 9.80228 17.1977 10.25 17.75 10.25C18.3023 10.25 18.75 10.6977 18.75 11.25V18.25C18.75 18.8023 18.3023 19.25 17.75 19.25H5.75C5.19772 19.25 4.75 18.8023 4.75 18.25V11.25C4.75 10.6977 5.19771 10.25 5.75 10.25C6.30229 10.25 6.75 9.80228 6.75 9.25C6.75 8.69772 6.30229 8.25 5.75 8.25H4.75C3.64543 8.25 2.75 9.14543 2.75 10.25V19.25C2.75 20.3546 3.64543 21.25 4.75 21.25H18.75C19.8546 21.25 20.75 20.3546 20.75 19.25V10.25C20.75 9.14543 19.8546 8.25 18.75 8.25Z" fill="currentColor"></path>
                      </svg>
                    </span>
                    Export CSV
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-header border-0 pt-6">
                <div className="card-title w-100">
                  <div className="d-flex flex-wrap align-items-center gap-3 my-1 w-100">
                    <div className="position-relative">
                      <span className="svg-icon svg-icon-1 position-absolute ms-6" style={{ top: '50%', transform: 'translateY(-50%)' }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="currentColor"></rect>
                          <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="currentColor"></path>
                        </svg>
                      </span>
                      <input
                        type="text"
                        className="form-control form-control-solid w-250px ps-15"
                        placeholder="Search Name, Email Or Phone.."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </div>
                    <DateRangePicker value={daterange} onChange={setDaterange} placeholder="Date Range" />
                    <div style={{ minWidth: 180 }}>
                      <Select
                        options={VERIFICATION_OPTIONS}
                        value={VERIFICATION_OPTIONS.find(o => o.value === verificationBadge) || VERIFICATION_OPTIONS[0]}
                        onChange={(opt) => setVerificationBadge(opt?.value || '')}
                        placeholder="Verification"
                        className="react-select-container fs-6"
                        classNamePrefix="react-select"
                      />
                    </div>
                    <div style={{ minWidth: 180 }}>
                      <Select
                        isClearable
                        options={countryOptions}
                        value={countryOptions.find(o => o.value === countryId) || null}
                        onChange={(opt) => setCountryId((opt as any)?.value ?? null)}
                        placeholder="All Countries"
                        className="react-select-container fs-6"
                        classNamePrefix="react-select"
                      />
                    </div>
                    {hasActiveFilters && (
                      <button type="button" onClick={handleClear} className="btn btn-sm btn-light-danger">
                        Clear
                      </button>
                    )}
                  </div>
                </div>
                <div className="card-toolbar">
                  <button type="button" className="btn btn-primary" onClick={() => navigate('/admin/users/add')}>
                    <span className="svg-icon svg-icon-2">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect opacity="0.5" x="11.364" y="20.364" width="16" height="2" rx="1" transform="rotate(-90 11.364 20.364)" fill="currentColor"></rect>
                        <rect x="4.36396" y="11.364" width="16" height="2" rx="1" fill="currentColor"></rect>
                      </svg>
                    </span>
                    Add User
                  </button>
                </div>
              </div>
              <div className="card-body pt-0">
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed fs-6 gy-5">
                    <thead>
                      <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                        <th className="w-10px pe-2">#</th>
                        <th className="min-w-125px">Image</th>
                        <th className="min-w-125px">Name</th>
                        <th className="min-w-125px">Email</th>
                        <th className="min-w-125px">Phone</th>
                        <th className="min-w-125px">Address</th>
                        <th className="min-w-125px">Verification</th>
                        <th className="min-w-75px">Status</th>
                        <th className="min-w-125px">Created Date</th>
                        <th className="min-w-75px">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="fw-semibold text-gray-600 fs-6">
                      {users.data.map((user: any, index: number) => (
                        <tr key={user.id} className="odd">
                          <td>{(users.current_page - 1) * 15 + index + 1}</td>
                          <td>
                            <div className="symbol symbol-40px overflow-hidden me-3">
                              <div className="symbol-label">
                                <Image 
                                  src={user.picture} 
                                  alt={user.name} 
                                  type="avatar"
                                  className="w-100 h-100 object-fit-cover"
                                />
                              </div>
                            </div>
                          </td>
                          <td>{user.name}</td>
                          <td>{user.email}</td>
                          <td>{user.phone || 'N/A'}</td>
                          <td>{user.city?.name || '--'} {user.country?.name || '--'}</td>
                          <td>
                            {user.verification_badge && user.verification_badge !== 'Unverified' ? (
                              <span className={`badge badge-light-${user.verification_badge.startsWith('Requested') ? 'warning' : 'success'}`}>
                                {user.verification_badge}
                              </span>
                            ) : (
                              <span className="badge badge-light-secondary text-dark">Unverified</span>
                            )}
                          </td>
                          <td>
                            <span 
                              className={`badge badge-light-${user.accStatus === 1 ? 'success' : 'danger'}`}
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleToggleStatus(user.id, user.accStatus)}
                              title="Click to toggle status"
                            >
                              {user.accStatus === 1 ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>{formatDateOnly(user.created_at)}</td>
                          <td className="text-center">
                            <ActionButtons
                              onView={() => navigate(`/admin/users/${user.id}`)}
                              onEdit={() => navigate(`/admin/users/${user.id}/edit`)}
                              onDelete={() => handleDelete(user.id)}
                              onToggleStatus={() => handleToggleStatus(user.id, user.accStatus)}
                              status={user.accStatus}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {users.data.length > 0 && (
                  <div className="card-footer border-0 pt-6">
                    <Pagination
                      currentPage={users.current_page}
                      lastPage={users.last_page}
                      total={users.total || 0}
                      perPage={users.per_page || 15}
                      onPageChange={(newPage) => setPage(newPage)}
                      showTotal={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;
